import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchProjects } from '@/features/projects/api/projectsApi'
import { useEffect, useState } from 'react'
import { Testimonials } from '@/components/Testimonials'
import { FeaturedDevelopers } from '@/components/FeaturedDevelopers'
import { MapPreview } from '@/components/MapPreview'
import { NewsletterSignup } from '@/components/NewsletterSignup'

export default function HomePage() {
  const [isVisible, setIsVisible] = useState({})

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['featured-projects'],
    queryFn: () => fetchProjects({ status: 'activated' }),
  })

  const projects = projectsData?.data?.slice(0, 6) || []

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }))
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-right md:bg-center"
          style={{
            backgroundImage: 'url(/nicebeaverheroimage3.jpeg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>

        <div className="relative z-10 container-custom py-24 pt-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 leading-tight">
              Find a home you love — before it's built.
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-6 font-light leading-relaxed">
              Track off-plan developments early, understand every detail, and secure the right home with confidence.
            </p>
            <div className="inline-flex items-center bg-coral-500 text-white px-6 py-3 rounded-lg mb-10 shadow-lg">
              <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex flex-col">
                <span className="font-medium text-lg">Selected homes up to 10% off registered valuations</span>
                <span className="text-sm text-white/90 mt-0.5">Best deals for early buyers — discounts reduce with each presale</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/projects"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-white/10 backdrop-blur-sm border-2 border-white hover:bg-white hover:text-primary-700 transition-all duration-300"
              >
                Browse projects
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center text-lg font-light text-white hover:text-white/80 transition-colors"
              >
                How it works
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discount Banner */}
      <section className="bg-gradient-to-r from-coral-500 to-coral-600 border-b-4 border-coral-700">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center text-white">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 mr-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-medium">Genuine discounts up to 10% off</div>
                <div className="text-white/90 text-sm">Benchmarked against registered valuations — not inflated list prices</div>
                <div className="text-white/90 text-sm font-semibold mt-1">⚡ Discounts reduce with each presale — early buyers get the best deals</div>
              </div>
            </div>
            <Link
              to="/projects"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-coral-600 bg-white hover:bg-secondary-50 transition-colors rounded-lg shadow-lg whitespace-nowrap"
            >
              See available homes
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-b border-secondary-200 bg-white">
        <div className="container-custom py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="border-r border-secondary-200 last:border-r-0">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-mint-100">
                <svg className="w-6 h-6 text-mint-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="font-medium text-secondary-900 mb-1">Independent platform</div>
              <div className="text-sm text-secondary-600">Buyer-first focus</div>
            </div>
            <div className="border-r border-secondary-200 last:border-r-0">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="font-medium text-secondary-900 mb-1">Free for buyers</div>
              <div className="text-sm text-secondary-600">No platform fees</div>
            </div>
            <div className="border-r border-secondary-200 last:border-r-0">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-sun-100">
                <svg className="w-6 h-6 text-sun-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="font-medium text-secondary-900 mb-1">Complete transparency</div>
              <div className="text-sm text-secondary-600">Clear pricing</div>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-coral-100">
                <svg className="w-6 h-6 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="font-medium text-secondary-900 mb-1">Take your time</div>
              <div className="text-sm text-secondary-600">No pressure</div>
            </div>
          </div>
          <p className="text-center text-sm text-secondary-500 mt-6 max-w-2xl mx-auto">
            Beaver exists to give buyers clarity and confidence when purchasing off-plan.
          </p>
        </div>
      </section>

      {/* Problem Framing Section */}
      <section className="py-24 bg-secondary-50">
        <div className="container-custom">
          <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-16 text-center max-w-3xl mx-auto leading-tight">
            Buying off-plan shouldn't feel risky or opaque.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" id="problems" data-animate>
            <div className="text-center bg-white p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-sun-100 rounded-full">
                <svg className="w-8 h-8 text-sun-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Too little information</h3>
              <p className="text-secondary-600 leading-relaxed">
                Floorplans, sunlight, storage, finishes often unclear.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-coral-100 rounded-full">
                <svg className="w-8 h-8 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Pressure too early</h3>
              <p className="text-secondary-600 leading-relaxed">
                Buyers pushed to decide before they feel ready.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-primary-100 rounded-full">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">No buyer-first platform</h3>
              <p className="text-secondary-600 leading-relaxed">
                Existing tools serve sellers, not people.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Beaver Works */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-4 text-center">
            How Beaver works
          </h2>
          <p className="text-xl text-secondary-600 text-center mb-12 max-w-2xl mx-auto">
            A clear path from discovery to settlement
          </p>

          {/* Image Placeholder: Product screenshots or process visual */}
          <div className="mb-20 rounded-lg overflow-hidden bg-gradient-to-r from-primary-50 via-mint-50 to-sun-50 p-12 border-2 border-primary-200">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-white shadow-lg">
                  <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-secondary-500 italic">
                  [Image: Clean interface mockup showing the 4-step process from discovery to settlement]
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6" id="how-it-works" data-animate>
            <div className="relative">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 mr-4">
                  <span className="text-2xl font-light text-white">1</span>
                </div>
                <div className="hidden md:block flex-1 h-px bg-primary-200 mt-6"></div>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Follow developments early</h3>
              <p className="text-secondary-600 leading-relaxed">
                Track projects while they are consented and designed. Get regular updates and be the first to know when they go on the market.
              </p>
            </div>

            <div className="relative">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 rounded-full bg-mint-500 flex items-center justify-center flex-shrink-0 mr-4">
                  <span className="text-2xl font-light text-white">2</span>
                </div>
                <div className="hidden md:block flex-1 h-px bg-mint-200 mt-6"></div>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Understand every detail</h3>
              <p className="text-secondary-600 leading-relaxed">
                Unit size, sun direction, heating, storage, schools, amenities.
              </p>
            </div>

            <div className="relative">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 rounded-full bg-coral-500 flex items-center justify-center flex-shrink-0 mr-4">
                  <span className="text-2xl font-light text-white">3</span>
                </div>
                <div className="hidden md:block flex-1 h-px bg-coral-200 mt-6"></div>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Reserve with confidence</h3>
              <p className="text-secondary-600 leading-relaxed">
                Secure a specific home with transparent pricing.
              </p>
            </div>

            <div className="relative">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 rounded-full bg-sun-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-light text-white">4</span>
                </div>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-3">Stay informed to settlement</h3>
              <p className="text-secondary-600 leading-relaxed">
                Milestones, updates, documents in one place.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <Link to="/projects" className="inline-flex items-center text-lg text-primary-600 hover:text-primary-700 transition-colors font-medium">
              Browse projects
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Real Buyer Value */}
      <section className="py-24 bg-white border-t border-secondary-200">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center bg-coral-100 text-coral-700 px-4 py-2 rounded-full mb-4">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                <span className="font-medium text-sm uppercase tracking-wide">Major drawcard</span>
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-4 text-center">
              Up to 10% off registered valuations
            </h2>
            <p className="text-xl text-secondary-600 text-center mb-16 max-w-3xl mx-auto">
              Selected homes on Beaver are offered with genuine discounts — benchmarked against registered valuations, not inflated list prices.
            </p>

            {/* Discount Value Proposition */}
            <div className="mb-16 rounded-xl overflow-hidden bg-gradient-to-br from-coral-500 to-coral-600 p-8 md:p-12 border-4 border-coral-700 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-light mb-2">10%</div>
                  <div className="text-white/90 text-lg">Maximum discount off registered valuations</div>
                </div>
                <div className="text-center border-l border-r border-white/30 md:border-0">
                  <div className="text-5xl md:text-6xl font-light mb-2">100%</div>
                  <div className="text-white/90 text-lg">Genuine — benchmarked against valuations</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-light mb-2">0%</div>
                  <div className="text-white/90 text-lg">Inflated prices or hidden fees</div>
                </div>
              </div>
              <div className="mt-8 text-center">
                <p className="text-sm text-white/80 italic">
                  [Image: Clean calculator or pricing comparison visual showing genuine savings]
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
              {/* Left Column - Why discounts exist */}
              <div>
                <h3 className="text-2xl font-medium text-secondary-900 mb-6">Why this value exists</h3>
                <div className="space-y-4 text-secondary-600 leading-relaxed">
                  <p>
                    <strong className="text-secondary-900">Up to 10% off registered valuations.</strong> Beaver works with developers to offer genuine pricing advantages to buyers who engage early and make informed decisions.
                  </p>
                  <p>
                    These discounts are benchmarked against registered valuations — not created through inflated list prices. What you see is what you save.
                  </p>
                  <p>
                    <strong className="text-secondary-900">Discounts reduce with each presale.</strong> The best deals go to early buyers who act first. Each time a unit is reserved, the discount percentage decreases for remaining units, rewarding those who commit early.
                  </p>
                  <p>
                    Developers reward early commitment. You get transparency and real value. Beaver does not inflate prices to create artificial discounts.
                  </p>
                </div>
              </div>

              {/* Right Column - How to use the discount */}
              <div>
                <h3 className="text-2xl font-medium text-secondary-900 mb-6">How you use the value is up to you</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-mint-100 flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                      <svg className="w-4 h-4 text-mint-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900 mb-1">Reduce purchase price</h4>
                      <p className="text-secondary-600 text-sm">Lower the sale price and reduce loan requirements</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-coral-100 flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                      <svg className="w-4 h-4 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900 mb-1">Upgrade finishes</h4>
                      <p className="text-secondary-600 text-sm">Apply value toward premium fixtures or customizations</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-sun-100 flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                      <svg className="w-4 h-4 text-sun-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900 mb-1">Offset settlement costs</h4>
                      <p className="text-secondary-600 text-sm">Cover legal fees, inspections, or moving expenses</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mr-4 mt-1">
                      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900 mb-1">Improve loan position</h4>
                      <p className="text-secondary-600 text-sm">Increase equity and strengthen your financial position</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust note */}
            <div className="mt-12 pt-8 border-t border-secondary-200">
              <div className="bg-coral-50 border-l-4 border-coral-500 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-coral-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-secondary-700 font-medium mb-1">
                      Genuine discounts up to 10% off registered valuations
                    </p>
                    <p className="text-sm text-secondary-600">
                      Discounts are offered at the developer's discretion and may be time-bound or unit-specific. All pricing is benchmarked against independent registered valuations, not inflated list prices.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/projects"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-coral-500 hover:bg-coral-600 transition-colors rounded-lg shadow-lg"
              >
                See homes with discounts
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center text-lg text-secondary-600 hover:text-primary-600 transition-colors font-medium"
              >
                How pricing works
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Persona Value Section */}
      <section className="py-24 bg-secondary-50">
        <div className="container-custom">
          <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-4 text-center">
            Built for real buyers, at different life stages.
          </h2>

          {/* Image Placeholders: Three lifestyle photos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 mb-8">
            <div className="aspect-[4/3] bg-secondary-100 rounded-lg overflow-hidden">
              <img
                src="/gemini_generated_image_firsthome.jpeg"
                alt="Young first-home buyers exploring floor plans together"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] bg-secondary-100 rounded-lg overflow-hidden">
              <img
                src="/gemini_generated_image_family.jpeg"
                alt="Family with young children looking at model home or development plans"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] bg-secondary-100 rounded-lg overflow-hidden">
              <img
                src="/gemini_generated_image_startover.jpeg"
                alt="Individual calmly reviewing property information on laptop"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16" id="personas" data-animate>
            <div className="bg-white p-10 border border-secondary-200 hover:border-primary-200 transition-colors">
              <h3 className="text-2xl font-medium text-secondary-900 mb-4">First-home buyers</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Plain-English explanations
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Finance-aware (no advice)
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Confidence-building UX
                </li>
              </ul>
              <Link to="/projects" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center">
                Browse projects
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="bg-white p-10 border border-secondary-200 hover:border-primary-200 transition-colors">
              <h3 className="text-2xl font-medium text-secondary-900 mb-4">Young families</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  School zones and amenities surfaced early
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Storage, space, liveability prioritised
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Long timelines made less stressful
                </li>
              </ul>
              <Link to="/projects" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center">
                Browse projects
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="bg-white p-10 border border-secondary-200 hover:border-primary-200 transition-colors">
              <h3 className="text-2xl font-medium text-secondary-900 mb-4">Starting again</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Privacy and discretion
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Clear information without pressure
                </li>
                <li className="flex items-start text-secondary-600">
                  <span className="text-secondary-400 mr-2">•</span>
                  Independent, adult tone
                </li>
              </ul>
              <Link to="/projects" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center">
                Browse projects
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Listings Preview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-2">
                Browse projects
              </h2>
              <p className="text-xl text-secondary-600">
                Available now and coming soon
              </p>
            </div>
            <Link
              to="/projects"
              className="hidden md:inline-flex items-center text-lg text-primary-600 hover:text-primary-700 transition-colors font-medium"
            >
              Browse all projects
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-secondary-200 border-t-primary-600 rounded-full animate-spin"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 text-secondary-300">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-secondary-900 mb-2">New projects coming soon</h3>
              <p className="text-secondary-600">
                Check back soon for new developments.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" id="listings" data-animate>
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group"
                >
                  <div className="aspect-[4/3] bg-secondary-100 overflow-hidden mb-4">
                    {project.image_urls?.[0] ? (
                      <img
                        src={project.image_urls[0]}
                        alt={project.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-16 h-16 text-secondary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-medium text-secondary-900 group-hover:text-primary-600 transition-colors">
                        {project.name}
                      </h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        project.status === 'activated'
                          ? 'bg-success-100 text-success-800'
                          : 'bg-warning-100 text-warning-800'
                      }`}>
                        {project.status === 'activated' ? 'Now reserving' : 'Coming soon'}
                      </span>
                    </div>
                    <p className="text-secondary-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {project.location}
                    </p>
                    {project.price_range_min && project.price_range_max && (
                      <p className="text-lg font-medium text-secondary-900">
                        ${project.price_range_min.toLocaleString()} – ${project.price_range_max.toLocaleString()}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12 md:hidden">
            <Link
              to="/projects"
              className="inline-flex items-center text-lg text-primary-600 hover:text-primary-700 transition-colors font-medium"
            >
              Browse all projects
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Map Preview */}
      <MapPreview />

      {/* Featured Developers */}
      <FeaturedDevelopers />

      {/* Independence & Compliance */}
      <section className="py-24 bg-secondary-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-8 text-center">
            Independent. Transparent. Buyer-first.
          </h2>

          <div className="space-y-6 text-lg text-secondary-600 leading-relaxed">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-mint-500 mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
              <p>Beaver is not a real estate agent — we're an independent platform</p>
            </div>
            <div className="flex items-start">
              <svg className="w-6 h-6 text-mint-500 mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
              <p>Buyers pay no fees to use the platform</p>
            </div>
            <div className="flex items-start">
              <svg className="w-6 h-6 text-mint-500 mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
              <p>Some developers work with agents, some sell directly — we support both and make it clear</p>
            </div>
            <div className="flex items-start">
              <svg className="w-6 h-6 text-mint-500 mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
              <p>No hidden incentives or misleading pricing</p>
            </div>
            <div className="flex items-start">
              <svg className="w-6 h-6 text-mint-500 mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
              </svg>
              <p>Designed to align with the NZ Real Estate Act</p>
            </div>
          </div>

          <div className="mt-10 p-6 bg-white rounded-lg border border-secondary-200">
            <h3 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center">
              <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              How commissions work
            </h3>
            <p className="text-secondary-600 leading-relaxed">
              If a developer works with a real estate agent for a project, standard real estate commission may apply (typically negotiated between the developer and agent).
              When developers sell directly without agents, there are no agent commissions. Each project page clearly indicates whether an agent is involved.
              <strong className="text-secondary-900"> You never pay Beaver any fees as a buyer.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup />

      {/* Final Conversion Section */}
      <section className="py-32 bg-white">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-primary-600 mb-6">
            A better way to buy off-plan.
          </h2>
          <p className="text-xl text-secondary-600 mb-10">
            Free to explore. No pressure to commit.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-10 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Get started
          </Link>
        </div>
      </section>
    </div>
  )
}
